import moment from 'moment';
import angular from 'angular';
import * as R from 'ramda';

export default function EditRecommendationDialogController(
  $q,
  $rootScope,
  $scope,
  $timeout,
  alerts,
  amDataService,
  amSharedModelService,
  loaderService,
  saveRecommendationConfirmDialog,
  ssDialogService
) {
  const vm = this;

  vm.handleCloseDialog = handleCloseDialog;
  vm.handleFormSubmit = handleFormSubmit;
  vm.handleNameChange = handleNameChange;
  vm.handleAssignmentChange = handleAssignmentChange;
  vm.handleStatusChange = handleStatusChange;
  vm.handleRepeatRecommendationChange = handleRepeatRecommendationChange;
  vm.handlePriorityChange = handlePriorityChange;
  vm.newRecommendation = { ...vm.recommendation };

  vm.nameIsValid = true;

  _activate();

  /** *********************************************
   * Event Handlers
   */

  function handleCloseDialog() {
    return _closeDialog();
  }

  function handleFormSubmit($event) {
    if ($scope.editAMRecommendationForm.$invalid) {
      return Promise.resolve();
    }
    if (!_isValid(true)) {
      // Set the form as unsubmitted since the user needs to make changes
      $scope.editAMRecommendationForm.$setPristine();
      $scope.editAMRecommendationForm.$setUntouched();
      return Promise.resolve();
    }
    _formatRecommendationInputData();
    if ((vm.recommendationsDefaultDueDate !== null) && (vm.dueDate > vm.recommendationsDefaultDueDate)) {
      return _openSaveRecommendationConfirmation($event);
    }
    const isNew = vm.newRecommendation.recommendationId <= 0;

    if (!isNew && angular.equals(vm.recommendation, vm.newRecommendation)) {
      // No need to save
      return _closeDialog();
    }
    loaderService.showViewLoader = true;
    return amSharedModelService.saveRecommendation(vm.newRecommendation).then((savedRecommendation) => {
      angular.extend(vm.newRecommendation, savedRecommendation);
      angular.extend(vm.recommendation, vm.newRecommendation);
      if (isNew) {
        vm.addCallback(vm.recommendation);
        $rootScope.$broadcast('refreshAMRecommendations', { recommendationCategoryId: vm.recommendation.recommendationCategoryId });
        $rootScope.$broadcast('refreshNotifications');
      }
      return _closeDialog().then(() => {
        $rootScope.$broadcast('refreshAMRecommendations', { recommendationCategoryId: vm.recommendation.recommendationCategoryId });
        // $rootScope.$broadcast('refreshAMRecommendation', { recommendationId: vm.recommendation.recommendationId });
        alerts.success('The recommendation has been saved successfully.');
        $rootScope.$broadcast('refreshNotifications');
      });
    })
    .catch(() => {
      alerts.error('An error occurred while saving the recommendation.');
    })
    .finally(() => {
      loaderService.showViewLoader = false;
    });
  }

  /**
   * Sync is repeat recommendation and priority
   */
  function handleRepeatRecommendationChange() {
    if (vm.newRecommendation.isRepeatRecommendation) { // Is a repeat recommendation
      vm.newRecommendation.priorityId = 3; // High Priority
    } else {
      vm.newRecommendation.priorityId = vm.recommendation.priorityId;
    }
  }

  /**
   * Sync is repeat recommendation and priority
   */
  function handlePriorityChange() {
    if (vm.newRecommendation.priorityId !== 3) { // High
      vm.newRecommendation.isRepeatRecommendation = false;
    }
  }

  /**
   * Sync statusId and assignedToUser to assignedToUserId
   */
  function handleAssignmentChange() {
    vm.newRecommendation.assignedToUserId = vm.newRecommendation.assignedToUser.userId;
    if ((vm.newRecommendation.statusId !== 3) && (vm.newRecommendation.statusId !== 4)) { // Completed, Completed - Risk Accepted
      if (vm.newRecommendation.assignedToUserId > 0) {
        vm.newRecommendation.statusId = 2; // Assigned
      } else {
        vm.newRecommendation.statusId = 1; // Unassigned
      }
    }
  }

  function handleNameChange() {
    const dfdSave = $q.defer();

    if (_isNameValid()) {
      amSharedModelService.checkRecommendationNameExists(
        vm.newRecommendation.recommendationCategoryId,
        vm.newRecommendation.recommendationId,
        vm.newRecommendation.name
      ).then((nameExists) => {
        if (nameExists) {
          alerts.error('The Recommendation Name exists already. Please choose a different name.');
          vm.nameIsValid = false;
          dfdSave.reject();
        } else {
          vm.nameIsValid = true;
          dfdSave.resolve();
        }
      });
    } else {
      vm.nameIsValid = false;
      dfdSave.reject();
    }

    return dfdSave.promise;
  }

  function handleStatusChange() {
    if (vm.newRecommendation.statusId === 1) { // Unassigned
      vm.completedDate = null;
      vm.newRecommendation.completedDate = null;
      vm.newRecommendation.assignedToUserId = -1;
    } else if (vm.newRecommendation.statusId === 2) { // Assigned
      vm.completedDate = null;
      vm.newRecommendation.completedDate = null;
      if (vm.newRecommendation.assignedToUserId > 0) {
        // Don't do anything. There is an assignment already
      } else {
        alerts.error('You cannot change the status to assigned. '
          + 'Please change the assigned user to update to the assigned status.');
        vm.newRecommendation.statusId = 1; // Unassigned
      }
    } else if (vm.newRecommendation.statusId === 3) { // Completed
      vm.completedDate = moment();
      vm.newRecommendation.completedDate = moment(vm.completedDate).format('MM/DD/YYYY');
    } else if (vm.newRecommendation.statusId === 4) { // Completed - Risk Accepted
      vm.completedDate = moment();
      vm.newRecommendation.completedDate = moment(vm.completedDate).format('MM/DD/YYYY');
    }
  }

  /** *********************************************
   * Private Functions
   */

  function _activate() {
    // Set Default Values
    if (R.isNil(vm.newRecommendation.assignedToUserId)) { vm.newRecommendation.assignedToUserId = -1; }
    if (R.isNil(vm.newRecommendation.businessRiskId)) { vm.newRecommendation.businessRiskId = -1; }
    if (R.isNil(vm.newRecommendation.technicalRiskId)) { vm.newRecommendation.technicalRiskId = -1; }
    if (R.isNil(vm.newRecommendation.priorityId)) { vm.newRecommendation.priorityId = -1; }
    if (!R.isNil(vm.newRecommendation.dueDate)) { vm.dueDate = moment(vm.newRecommendation.dueDate); }
    if (!R.isNil(vm.newRecommendation.completedDate)) { vm.completedDate = moment(vm.newRecommendation.completedDate); }
    if (!R.isNil(vm.newRecommendation.customDate1)) { vm.customDate1 = moment(vm.newRecommendation.customDate1); }
    if (!R.isNil(vm.newRecommendation.customDate2)) { vm.customDate2 = moment(vm.newRecommendation.customDate2); }
    if (!R.isNil(vm.newRecommendation.customDate3)) { vm.customDate3 = moment(vm.newRecommendation.customDate3); }
    if (!R.isNil(vm.newRecommendation.customDate4)) { vm.customDate4 = moment(vm.newRecommendation.customDate4); }
    if (!R.isNil(vm.newRecommendation.customDate5)) { vm.customDate5 = moment(vm.newRecommendation.customDate5); }
    vm.contentPaneIsFaded = false;
    vm.currentRecommendationId = 0;

    _initEventListeners();

    loaderService.showViewLoader = true;
    _refreshBusinessRisks().then(
      _refreshTechnicalRisks().then(
        amDataService.getBaseAudit(vm.auditId).then((response) => {
          loaderService.showViewLoader = false;
          const fddd = response.data.recommendationsDefaultDueDate;
          if (fddd !== null) { vm.recommendationsDefaultDueDate = moment(fddd); }
        })
      )
    );
  }

  function _initEventListeners() {
    $scope.$on('refreshAMBusinessRisks', _refreshBusinessRisks);
    $scope.$on('refreshAMTechnicalRisks', _refreshTechnicalRisks);
    $scope.$on('refreshAMPriorities', _refreshPriorities);
  }

  function _formatRecommendationInputData() {
    vm.newRecommendation.dueDate = formatDate(vm.dueDate, vm.newRecommendation.dueDate);
    vm.newRecommendation.completedDate = formatDate(vm.completedDate, vm.newRecommendation.completedDate);
    vm.newRecommendation.customDate1 = formatDate(vm.customDate1, vm.newRecommendation.customDate1);
    vm.newRecommendation.customDate2 = formatDate(vm.customDate2, vm.newRecommendation.customDate2);
    vm.newRecommendation.customDate3 = formatDate(vm.customDate3, vm.newRecommendation.customDate3);
    vm.newRecommendation.customDate4 = formatDate(vm.customDate4, vm.newRecommendation.customDate4);
    vm.newRecommendation.customDate5 = formatDate(vm.customDate5, vm.newRecommendation.customDate5);
    vm.newRecommendation.assignedToUserId = getDefaultedId(vm.newRecommendation.assignedToUserId);
    vm.newRecommendation.businessRiskId = getDefaultedId(vm.newRecommendation.businessRiskId);
    vm.newRecommendation.technicalRiskId = getDefaultedId(vm.newRecommendation.technicalRiskId);
    vm.newRecommendation.priorityId = getDefaultedId(vm.newRecommendation.priorityId);

    function formatDate(vmDate) {
      if (!R.isNil(vmDate)) {
        return moment(vmDate).format('MM/DD/YYYY');
      }
      return null;
    }

    function getDefaultedId(id) {
      return id === -1 ? null : id;
    }
  }

  function _refreshBusinessRisks() {
    const dfdRefresh = $q.defer();
    vm.businessRisks = amSharedModelService.businessRisks;
    dfdRefresh.resolve();
    return dfdRefresh.promise;
  }

  function _refreshTechnicalRisks() {
    const dfdRefresh = $q.defer();
    vm.technicalRisks = amSharedModelService.technicalRisks;
    dfdRefresh.resolve();
    return dfdRefresh.promise;
  }

  function _refreshPriorities() {
    const dfdRefresh = $q.defer();
    vm.priorities = amSharedModelService.priorities;
    dfdRefresh.resolve();
    return dfdRefresh.promise;
  }

  function _isValid(checkName) {
    let isValid = true;

    angular.forEach($scope.editAMRecommendationForm.$error.pattern, () => {
      isValid = false;
    });
    angular.forEach($scope.editAMRecommendationForm.$error.required, () => {
      isValid = false;
    });

    if (isValid) {
      isValid = _areDatesValid();
    }

    if (isValid) {
      if (!_hasBothAssignedUserAndDueDateOrNeither()) {
        alerts.error('If the Due Date or Assigned User is entered, then both must be set in the Recommendation.');
        isValid = false;
      }
    }

    if (isValid && checkName) {
      if (!vm.nameIsValid) {
        alerts.error('There is an issue with the Recommendation Name.');
        isValid = false;
      }
    }

    return isValid;
  }

  function _areDatesValid() {
    let isValid = true;
    angular.forEach($scope.editAMRecommendationForm.$error.parse, (item) => {
      if ((item.$name === 'editRecommendationCompletedDate')
        || (item.$name === 'editRecommendationDueDate')
        || (item.$name === 'editRecommendationCustomDate1')
        || (item.$name === 'editRecommendationCustomDate2')
        || (item.$name === 'editRecommendationCustomDate3')
        || (item.$name === 'editRecommendationCustomDate4')
        || (item.$name === 'editRecommendationCustomDate5')) {
        if (!R.isNil(item.$modelValue) && (!moment(item.$modelValue).isValid())) {
          isValid = false;
        }
      } else {
        isValid = false;
      }
    });
    return isValid;
  }

  function _isNameValid() {
    let isValid = true;

    if (!vm.nameIsValid) {
      alerts.error('There is an issue with the Recommendation Name.');
      isValid = false;
    }

    return isValid;
  }

  function _hasBothAssignedUserAndDueDateOrNeither() {
    // Is assigned to user
    const a = vm.newRecommendation.assignedToUserId && vm.newRecommendation.assignedToUserId > -1;
    // Has a due date
    const b = !!vm.dueDate;

    if (a || b) return a && b; // has both?
    return true; // has neither
  }

  function _openSaveRecommendationConfirmation($event) {
    loaderService.showViewLoader = true;
    ssDialogService.dialogs.auditTrail.saveRecommendationConfirmDialog = ssDialogService.alert(
      saveRecommendationConfirmDialog.getOptions($event, {
        customerId: vm.customerId,
        auditId: vm.auditId,
        statuses: vm.statuses,
        businessRisks: vm.businessRisks,
        technicalRisks: vm.technicalRisks,
        priorities: vm.priorities,
        users: vm.users,
        recommendation: vm.newRecommendation,
      })
    );
    ssDialogService.show(ssDialogService.dialogs.auditTrail.saveRecommendationConfirmDialog);
    ssDialogService.hide(ssDialogService.dialogs.auditTrail.editRecommendationDialog);
  }

  function _closeDialog() {
    return ssDialogService.hide(ssDialogService.dialogs.auditTrail.editRecommendationDialog);
  }
}

EditRecommendationDialogController.$inject = [
  '$q',
  '$rootScope',
  '$scope',
  '$timeout',
  'alerts',
  'amDataService',
  'amSharedModelService',
  'loaderService',
  'saveRecommendationConfirmDialog',
  'ssDialogService'
];
